import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Input,
  Label,
  Spinner,
  makeStyles,
  tokens,
  Text,
  Field,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  CheckmarkCircle24Filled,
  ErrorCircle24Filled,
  Info24Regular,
} from '@fluentui/react-icons';
import { adminService, AppConfiguration } from '../services/adminService';

const useStyles = makeStyles({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  urlField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  testButton: {
    marginTop: '8px',
  },
  statusMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    borderRadius: '4px',
    fontSize: '14px',
  },
  successMessage: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  errorMessage: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  infoMessage: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    color: tokens.colorPaletteBlueForeground2,
  },
  currentConfig: {
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '4px',
    fontSize: '12px',
  },
  configLabel: {
    fontWeight: '600',
    marginBottom: '4px',
  },
  configValue: {
    color: tokens.colorNeutralForeground3,
    wordBreak: 'break-all',
  },
  lastUpdated: {
    marginTop: '8px',
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
});

interface AdminSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

export const AdminSettingsModal: React.FC<AdminSettingsModalProps> = ({ open, onClose }) => {
  const styles = useStyles();
  const [url, setUrl] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [currentConfig, setCurrentConfig] = useState<AppConfiguration | null>(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveMessage, setSaveMessage] = useState<{ success: boolean; message: string } | null>(null);

  // Load current configuration when modal opens
  useEffect(() => {
    if (open) {
      loadCurrentConfig();
    }
  }, [open]);

  const loadCurrentConfig = async () => {
    setLoading(true);
    try {
      const config = await adminService.getConfiguration();
      setCurrentConfig(config);
      setUrl(config.copilotStudioUrl);
      setCompanyCode(config.companyCode);
    } catch (error) {
      console.error('Failed to load configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!url.trim()) {
      setTestResult({ success: false, message: 'Please enter a URL' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const result = await adminService.testConnection(url);
      setTestResult({
        success: result.isSuccessful,
        message: result.isSuccessful
          ? `Connection successful!${result.agentName ? ` Connected to: ${result.agentName}` : ''}`
          : `Connection failed: ${result.message}`,
      });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || 'Failed to test connection',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!url.trim()) {
      setSaveMessage({ success: false, message: 'URL cannot be empty' });
      return;
    }

    if (!companyCode.trim()) {
      setSaveMessage({ success: false, message: 'Company code cannot be empty' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      await Promise.all([
        adminService.updateCopilotStudioUrl(url),
        adminService.updateCompanyCode(companyCode),
      ]);
      setSaveMessage({ success: true, message: 'Configuration updated successfully!' });

      // Reload configuration after save
      setTimeout(() => {
        loadCurrentConfig();
        setSaveMessage(null);
      }, 2000);
    } catch (error: any) {
      setSaveMessage({
        success: false,
        message: error.message || 'Failed to update configuration',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setTestResult(null);
    setSaveMessage(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
              />
            }
          >
            Agent Configuration
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spinner label="Loading configuration..." />
              </div>
            ) : (
              <>
                {/* Current Configuration */}
                {currentConfig && (
                  <div className={styles.currentConfig}>
                    <div className={styles.configLabel}>Current Agent URL</div>
                    <div className={styles.configValue}>{currentConfig.copilotStudioUrl}</div>
                    <div className={styles.configLabel} style={{ marginTop: '8px' }}>Current Company Code</div>
                    <div className={styles.configValue}>{currentConfig.companyCode || 'USMF (default)'}</div>
                    <div className={styles.lastUpdated}>
                      Last updated by {currentConfig.lastUpdatedBy} on{' '}
                      {currentConfig.lastUpdatedAt
                        ? new Date(currentConfig.lastUpdatedAt).toLocaleString()
                        : 'Never'}
                    </div>
                  </div>
                )}

                {/* URL Input */}
                <Field label="Copilot Studio Agent URL" required>
                  <Input
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://....powerplatform.com/..."
                    disabled={saving}
                  />
                </Field>

                {/* Company Code Input */}
                <Field label="Company Code" required>
                  <Input
                    value={companyCode}
                    onChange={(e) => setCompanyCode(e.target.value)}
                    placeholder="USMF"
                    disabled={saving}
                  />
                </Field>

                {/* Test Connection Button */}
                <Button
                  appearance="secondary"
                  onClick={handleTestConnection}
                  disabled={testing || saving || !url.trim()}
                  className={styles.testButton}
                >
                  {testing ? <Spinner size="tiny" /> : 'Test Connection'}
                </Button>

                {/* Test Result Message */}
                {testResult && (
                  <div
                    className={`${styles.statusMessage} ${
                      testResult.success ? styles.successMessage : styles.errorMessage
                    }`}
                  >
                    {testResult.success ? (
                      <CheckmarkCircle24Filled />
                    ) : (
                      <ErrorCircle24Filled />
                    )}
                    <Text>{testResult.message}</Text>
                  </div>
                )}

                {/* Save Result Message */}
                {saveMessage && (
                  <div
                    className={`${styles.statusMessage} ${
                      saveMessage.success ? styles.successMessage : styles.errorMessage
                    }`}
                  >
                    {saveMessage.success ? (
                      <CheckmarkCircle24Filled />
                    ) : (
                      <ErrorCircle24Filled />
                    )}
                    <Text>{saveMessage.message}</Text>
                  </div>
                )}

                {/* Info Message */}
                <div className={`${styles.statusMessage} ${styles.infoMessage}`}>
                  <Info24Regular />
                  <Text>Configure which Copilot Studio agent to use. Changes apply immediately.</Text>
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSave}
              disabled={saving || loading || !url.trim() || !companyCode.trim()}
            >
              {saving ? <Spinner size="tiny" /> : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
